import api from './api';
import { supplierService } from './supplierService';
import { productService } from './productService';
import { serviceService } from './serviceService';
import { getProjects } from './projectsService';
import { getWarehouses } from './warehousesService';

const supplyContinuousDocumentService = {
  /**
   * Obtener todos los documentos por suministro continuo
   */
  getDocuments: async (params = {}) => {
    try {
      const response = await api.get('/documents', { 
        params: { ...params, document_type: 'suministro_continuo' } 
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener documentos por suministro continuo:', error);
      return {
        success: false,
        message: 'Error al obtener documentos',
        data: []
      };
    }
  },

  /**
   * Obtener un documento por ID
   */
  getDocument: async (id) => {
    try {
      const response = await api.get(`/documents/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener documento:', error);
      return {
        success: false,
        message: 'Error al obtener documento'
      };
    }
  },

  /**
   * Crear un nuevo documento
   */
  createDocument: async (data) => {
    try {
      const submitData = {
        ...data,
        // IMPORTANTE: Para documentos de suministro continuo, el document_type SIEMPRE debe ser 'suministro_continuo'
        // El campo "Tipo de Documento" del formulario se guarda en real_document_type para mostrarlo
        document_type: 'suministro_continuo',
        real_document_type: data.document_type || 'factura', // Guardar el tipo real seleccionado por el usuario
        status: 'open', // Los documentos por suministro continuo se crean como 'open'
      };
      const response = await api.post('/documents', submitData);
      return response.data;
    } catch (error) {
      console.error('Error al crear documento:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Error al crear documento'
      };
    }
  },

  /**
   * Actualizar un documento
   */
  updateDocument: async (id, data) => {
    try {
      const response = await api.put(`/documents/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar documento:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Error al actualizar documento'
      };
    }
  },

  /**
   * Actualizar solo cabecera del documento (fecha emisión, serie, número, observaciones)
   */
  updateDocumentHeader: async (id, data) => {
    try {
      const response = await api.patch(`/documents/${id}/header`, data);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar cabecera del documento:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Error al actualizar el documento'
      };
    }
  },

  /**
   * Eliminar un documento
   */
  deleteDocument: async (id) => {
    try {
      const response = await api.delete(`/documents/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar documento:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Error al eliminar documento'
      };
    }
  },

  /**
   * Agregar item a un documento
   */
  addItem: async (documentId, itemData) => {
    try {
      // Calcular totales del item
      const quantity = parseFloat(itemData.quantity || 0);
      const unitPrice = parseFloat(itemData.unit_price || 0);
      const subtotal = quantity * unitPrice;
      const hasIgv = itemData.has_igv !== false;
      const taxRate = hasIgv ? (parseFloat(itemData.tax_rate) || 18) : 0;
      const rate = taxRate;
      const taxAmount = hasIgv ? subtotal * (rate === 10.5 ? 0.105 : rate === 10 ? 0.10 : 0.18) : 0;
      const total = subtotal + taxAmount;

      const submitData = {
        ...itemData,
        quantity,
        unit_price: unitPrice,
        subtotal,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        total,
      };

      const response = await api.post(`/documents/${documentId}/items`, submitData);
      return response.data;
    } catch (error) {
      console.error('Error al agregar item:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Error al agregar item',
        errors: error.response?.data?.errors || {}
      };
    }
  },

  /**
   * Actualizar item de un documento
   */
  updateItem: async (documentId, itemId, itemData) => {
    try {
      // Calcular totales del item
      const quantity = parseFloat(itemData.quantity || 0);
      const unitPrice = parseFloat(itemData.unit_price || 0);
      const subtotal = quantity * unitPrice;
      const hasIgv = itemData.has_igv !== false;
      const taxRate = hasIgv ? (parseFloat(itemData.tax_rate) || 18) : 0;
      const rate = taxRate;
      const taxAmount = hasIgv ? subtotal * (rate === 10.5 ? 0.105 : rate === 10 ? 0.10 : 0.18) : 0;
      const total = subtotal + taxAmount;

      const submitData = {
        ...itemData,
        quantity,
        unit_price: unitPrice,
        subtotal,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        total,
      };

      const response = await api.put(`/documents/${documentId}/items/${itemId}`, submitData);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar item:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Error al actualizar item',
        errors: error.response?.data?.errors || {}
      };
    }
  },

  /**
   * Eliminar item de un documento
   */
  deleteItem: async (documentId, itemId) => {
    try {
      const response = await api.delete(`/documents/${documentId}/items/${itemId}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar item:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Error al eliminar item'
      };
    }
  },

  /**
   * Obtener proveedores (para formulario)
   */
  getSuppliers: async () => {
    try {
      const response = await supplierService.getSuppliers({ active_only: true, entity_provider: true });
      return response;
    } catch (error) {
      console.error('Error al obtener proveedores:', error);
      return {
        success: false,
        data: []
      };
    }
  },

  /**
   * Obtener productos (para formulario)
   */
  getProducts: async () => {
    try {
      const response = await productService.getProducts({ active_only: true });
      return response;
    } catch (error) {
      console.error('Error al obtener productos:', error);
      return {
        success: false,
        data: []
      };
    }
  },

  /**
   * Obtener servicios (para formulario)
   */
  getServices: async () => {
    try {
      const response = await serviceService.getServices({ active_only: true });
      return response;
    } catch (error) {
      console.error('Error al obtener servicios:', error);
      return {
        success: false,
        data: []
      };
    }
  },

  /**
   * Obtener proyectos (para formulario)
   */
  getProjects: async () => {
    try {
      const response = await getProjects({ active_only: true });
      return response;
    } catch (error) {
      console.error('Error al obtener proyectos:', error);
      return {
        success: false,
        data: []
      };
    }
  },

  /**
   * Obtener almacenes (para formulario)
   */
  getWarehouses: async () => {
    try {
      const response = await getWarehouses({ active_only: true });
      return response;
    } catch (error) {
      console.error('Error al obtener almacenes:', error);
      return {
        success: false,
        data: []
      };
    }
  },
};

export default supplyContinuousDocumentService;
